import React from "react";
import {
  Container,
  Box,
  Paper,
  Typography,
  Chip,
  CircularProgress,
  Alert,
} from "@mui/material";
import { faCommentCaptions } from "@fortawesome/pro-solid-svg-icons";
import CcpAdminBreadCrumbs from "../../Layout/CcpAdminBreadCrumbs";
import useFirestore from "../../../config/fireBaseHook";
import { formatDateTime } from "../../../utils/formatDate";

const STATUSES = ["Open", "In Progress", "Complete"];

const STATUS_COLOR = {
  Open: "warning",
  "In Progress": "info",
  Complete: "success",
};

const CcpAdminSchoolFeedback = () => {
  const pageName = "School Feedback";
  const icon = faCommentCaptions;

  const { data: feedback, loading, error, updateData } = useFirestore(
    "CcpBetaFeedback",
    "created_at",
    "desc",
  );

  const handleStatusChange = async (id, status) => {
    try {
      await updateData(id, { status });
    } catch (err) {
      console.error("Error updating feedback status:", err);
    }
  };

  return (
    <Container maxWidth="md" sx={{ paddingTop: "50px", pb: 6 }}>
      <CcpAdminBreadCrumbs pageName={pageName} icon={icon} />

      <Typography variant="body2" color="text.secondary" mb={3}>
        Feedback and bug reports submitted by beta testers. Click a status
        chip to update where an item stands.
      </Typography>

      {loading ? (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error">{error}</Alert>
      ) : feedback.length === 0 ? (
        <Alert severity="info">No feedback has been submitted yet.</Alert>
      ) : (
        feedback.map((item) => {
          const currentStatus = item.status || "Open";
          return (
            <Paper key={item.id} elevation={2} sx={{ p: 2, mb: 2 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  flexWrap: "wrap",
                  gap: 1,
                }}
              >
                <Box>
                  <Typography variant="subtitle1" fontWeight="bold">
                    {item.school || "Unknown School"}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {item.name || "Unknown"}
                    {item.location ? ` • ${item.location}` : ""}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Chip
                    label={item.type || "Bug"}
                    size="small"
                    color={item.type === "Bug" ? "error" : "default"}
                    variant="outlined"
                  />
                  <Typography variant="caption" color="text.secondary">
                    {formatDateTime(item.created_at)}
                  </Typography>
                </Box>
              </Box>

              <Typography variant="body1" sx={{ mt: 1.5, mb: 2 }}>
                {item.message}
              </Typography>

              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                {STATUSES.map((status) => (
                  <Chip
                    key={status}
                    label={status}
                    color={STATUS_COLOR[status]}
                    variant={currentStatus === status ? "filled" : "outlined"}
                    onClick={() => handleStatusChange(item.id, status)}
                    sx={{ cursor: "pointer" }}
                  />
                ))}
              </Box>
            </Paper>
          );
        })
      )}
    </Container>
  );
};

export default CcpAdminSchoolFeedback;
